import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { DateService } from '../../../shared/services/date.service';
import { ImageService } from '../../../shared/services/image.service';
import { AuthService } from '../../../shared/services/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ViewChild, TemplateRef } from '@angular/core';
import { UtilService } from '../../../shared/services/util.service';

@Component({
  standalone: false,
  selector: 'app-customer-details',
  templateUrl: './customer-details.component.html',
  styleUrl: './customer-details.component.scss'
})
export class CustomerDetailsComponent implements OnInit {

  customerId: string = '';
  selectedCustomer: any = null;
  customerData: any = null;
  beneficiaries: any[] = [];
  isLoading = true;

  // Freeze modal
  @ViewChild('freezeModal') freezeModal!: TemplateRef<any>;
  freezeModalData: any = null;
  freezeReason: string = '';
  otherReason: string = '';
  freezeNote: string = '';
  freezeModalRef: any = null;

  // Credit Balance modal
  @ViewChild('creditBalanceModal') creditBalanceModal!: TemplateRef<any>;
  creditAmount: number | null = null;
  creditCurrency: string = 'EUR';
  creditReason: string = '';
  creditPassword: string = '';
  creditError: string = '';
  creditSuccess: string = '';
  isCrediting: boolean = false;
  creditBalanceModalRef: any = null;
  /**
   * Idempotency key for the in-flight credit. Held across retries of the SAME credit so a
   * resubmit after a network drop replays the original response instead of crediting twice,
   * and cleared once the server gives a definitive answer so the next credit gets a new key.
   */
  private creditIdempotencyKey: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _api: ApiService,
    private _date: DateService,
    private _imageService: ImageService,
    public _auth: AuthService,
    private modalService: NgbModal,
    private _util: UtilService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.customerId = params['id'];
      if (this.customerId) {
        this.getCustomerDetails();
      }
    });
  }

  getCustomerDetails() {
    const vm = this;
    vm._api.getData('/admin/profile/' + vm.customerId).subscribe((res: any) => {
      vm.isLoading = false;
      if (res && !res.error) {
        vm.customerData = res.data;
        // If selectedCustomer wasn't found in list, use profile data
        if (!vm.selectedCustomer && vm.customerData) {
          vm.selectedCustomer = vm.customerData.customer;
        }
      }
    }, () => {
      vm.isLoading = false;
    });
    vm.loadBeneficiaries();
  }

  loadBeneficiaries() {
    const vm = this;
    vm._api.getData('/admin/customers/' + vm.customerId + '/beneficiaries').subscribe((res: any) => {
      if (res && !res.error) {
        vm.beneficiaries = Array.isArray(res.data) ? res.data : [];
      }
    });
  }

  getBeneficiaryDetailKeys(detailsObject: any): string[] {
    if (!detailsObject) return [];
    return Object.keys(detailsObject).filter(k => k !== 'bankId' && k !== 'countryId' && k !== 'logoUrl');
  }

  getDynamicFieldKeys(dynamicFields: any): string[] {
    if (!dynamicFields) return [];
    const excludeKeys = ['Transaction Type','Bank NameImageUrl','Beneficiary Account Number','Account Number','Mobile Number','Beneficiary Name'];
    return Object.keys(dynamicFields).filter(k => !excludeKeys.includes(k));
  }

  getFirstFieldValue(obj: any): string {
    if (!obj) return '';
    const keys = Object.keys(obj);
    return keys.length ? obj[keys[0]] : '';
  }

  getFirstCustomerFieldValue(): string {
    return this.getFirstFieldValue(this.customerData?.customerFields);
  }

  goBack() {
    this.router.navigate(['/app/customers']);
  }

  viewBeneficiaryTransactions(beneficiary: any) {
    this.router.navigate(['/app/customers/beneficiary-transactions'], {
      queryParams: {
        customerId: this.customerId,
        beneficiaryId: beneficiary._id
      }
    });
  }

  dateFormat(value: any) {
    return this._date.getDDMMYYYYfromEPOC(value);
  }

  formatDob(dob: number, format?: string): string {
    if (!dob) return '—';
    const date = new Date(dob);
    if (isNaN(date.getTime())) return '—';
    let d = String(date.getDate()).padStart(2, '0');
    let m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear().toString();
    const fmt = format || 'DD-MM-YYYY';
    if (fmt === 'MM-DD-YYYY') return `${m}/${d}/${y}`;
    if (fmt === 'YYYY-MM-DD') return `${y}/${m}/${d}`;
    return `${d}/${m}/${y}`;
  }

  getImage(id: any) {
    if (id && id.startsWith('http')) {
      id = new URL(id).pathname.split('/').pop();
    }
    this._imageService.fetchAndViewDoc(id);
  }

  formatMobile(mobile: string): string {
    if (!mobile) return '';
    
    const cleanMobile = mobile.replace(/[\s-]/g, '');
    const dialingCode = this.selectedCustomer?.country?.dialingCode;
    
    if (dialingCode) {
      const numberPart = cleanMobile.replace(dialingCode, '');
      if (numberPart && numberPart !== cleanMobile) {
        return `${dialingCode} ${numberPart}`;
      }
    }
    
    if (cleanMobile.startsWith('+91')) {
      return '+91 ' + cleanMobile.slice(3);
    } else {
      const countryCodeMatch = cleanMobile.match(/^(\+\d{1,3})(.*)$/);
      if (countryCodeMatch && countryCodeMatch[2]) {
        return `${countryCodeMatch[1]} ${countryCodeMatch[2]}`;
      }
    }
    
    return mobile;
  }

  formatBeneficiaryMobile(mobile: string, beneficiary: any): string {
    if (!mobile) return '';
    
    const cleanMobile = mobile.replace(/[\s-]/g, '');
    const dialingCode = beneficiary?.country?.dialingCode;
    
    if (dialingCode) {
      const numberPart = cleanMobile.replace(dialingCode, '');
      if (numberPart && numberPart !== cleanMobile) {
        return `${dialingCode} ${numberPart}`;
      }
    }
    
    if (cleanMobile.startsWith('+91')) {
      return '+91 ' + cleanMobile.slice(3);
    } else {
      const countryCodeMatch = cleanMobile.match(/^(\+\d{1,3})(.*)$/);
      if (countryCodeMatch && countryCodeMatch[2]) {
        return `${countryCodeMatch[1]} ${countryCodeMatch[2]}`;
      }
    }
    
    return mobile;
  }

  toggleAccountStatus(customer: any) {
    // If customer is frozen, directly unfreeze without modal
    if (customer.isFrozen) {
      const payload = {
        customerId: customer._id,
        reason: 'Account unfreeze',
        note: 'Direct unfreeze action'
      };

      this._api.postData('/admin/account-freeze/unfreeze', payload).subscribe({
        next: (response) => {
          if (!response.error) {
            customer.isFrozen = false;
            alert('Account successfully unfrozen!');
          } else {
            alert(`Error: ${response.message || 'Operation failed'}`);
          }
        },
        error: (error) => {
          alert('Error: ' + (error.message || error));
        }
      });
    } else {
      // If customer is active, show modal for freeze
      this._api.getData(`/admin/account-freeze/${customer._id}/status`).subscribe({
        next: (response) => {
          this.freezeModalData = {
            customerId: customer._id,
            customerName: customer.name,
            isFrozen: response.data?.isFrozen ?? customer.isFrozen ?? false
          };
          this.freezeReason = '';
          this.otherReason = '';
          this.freezeNote = '';
          this.freezeModalRef = this.modalService.open(this.freezeModal, { backdrop: 'static', size: 'md' });
        },
        error: () => {
          this.freezeModalData = {
            customerId: customer._id,
            customerName: customer.name,
            isFrozen: customer.isFrozen || false
          };
          this.freezeReason = '';
          this.otherReason = '';
          this.freezeNote = '';
          this.freezeModalRef = this.modalService.open(this.freezeModal, { backdrop: 'static', size: 'md' });
        }
      });
    }
  }

  closeFreezeModal() {
    if (this.freezeModalRef) {
      this.freezeModalRef.close();
    }
  }

  confirmFreezeAction() {
    const finalReason = this.freezeReason === 'Other' ? this.otherReason : this.freezeReason;
    if (!finalReason) return;

    const payload = {
      customerId: this.freezeModalData.customerId,
      reason: finalReason,
      note: this.freezeNote || ''
    };

    const endpoint = this.freezeModalData.isFrozen
      ? '/admin/account-freeze/unfreeze'
      : '/admin/account-freeze/freeze';

    this._api.postData(endpoint, payload).subscribe({
      next: (response) => {
        if (!response.error) {
          this.selectedCustomer.isFrozen = !this.freezeModalData.isFrozen;
          this.closeFreezeModal();
          alert(`Account successfully ${this.freezeModalData.isFrozen ? 'unfrozen' : 'frozen'}!`);
        } else {
          alert(`Error: ${response.message || 'Operation failed'}`);
        }
      },
      error: (error) => {
        alert('Error: ' + (error.message || error));
      }
    });
  }

  openCreditBalanceModal() {
    this.creditAmount = null;
    this.creditCurrency = 'EUR';
    this.creditReason = '';
    this.creditPassword = '';
    this.creditError = '';
    this.creditSuccess = '';
    this.isCrediting = false;
    // A newly opened modal is a new operation, never a retry of a previous one.
    this.creditIdempotencyKey = null;
    this.creditBalanceModalRef = this.modalService.open(this.creditBalanceModal, { backdrop: 'static', size: 'md' });
  }

  closeCreditBalanceModal() {
    if (this.creditBalanceModalRef) {
      this.creditBalanceModalRef.close();
    }
  }

  confirmCreditBalance() {
    if (!this.creditAmount || this.creditAmount <= 0 || !this.creditPassword) return;
    // Guard against a double-click racing two claims for the same key (the server would
    // answer the second with a 409).
    if (this.isCrediting) return;

    this.creditError = '';
    this.creditSuccess = '';
    this.isCrediting = true;

    const payload = {
      customerId: this.customerId,
      amount: this.creditAmount,
      currency: this.creditCurrency || 'EUR',
      reason: this.creditReason || 'Manual admin balance credit',
      currentPassword: this.creditPassword
    };

    // `/admin/credit-balance` is guarded by `requireIdempotencyKeyAtomic`, which rejects the
    // request outright without this header. Reuse the existing key if this is a retry of the
    // same credit — that is what stops a retry from crediting the customer twice.
    if (!this.creditIdempotencyKey) {
      this.creditIdempotencyKey = this._util.newIdempotencyKey();
    }

    this._api.postData('/admin/credit-balance', payload, {
      headers: { 'idempotency-key': this.creditIdempotencyKey }
    }).subscribe({
      next: (response: any) => {
        this.isCrediting = false;
        // The server has given a definitive answer, so this key is spent either way:
        // a further credit is a new operation and must not replay this response.
        this.creditIdempotencyKey = null;
        if (!response.error) {
          this.creditSuccess = 'Balance credited successfully!';
          this.creditPassword = '';
          // Refresh account data
          this._api.getData('/admin/profile/' + this.customerId).subscribe((res: any) => {
            if (res && !res.error) {
              this.customerData = res.data;
              this.selectedCustomer = this.customerData.customer;
            }
          });
        } else {
          this.creditError = response.message || 'Failed to credit balance';
        }
      },
      error: (error: any) => {
        this.isCrediting = false;
        // Deliberately keep `creditIdempotencyKey`: the outcome is unknown (the credit may
        // have been applied before the connection dropped), so a resubmit must carry the
        // same key and be deduplicated rather than double-crediting.
        this.creditError =
          (typeof error === 'string' ? error : null) ||
          error?.error?.message ||
          error?.error?.errors?.[0]?.msg ||
          error?.message ||
          'Failed to credit balance';
      }
    });
  }
}
